import React, { useState, useMemo } from 'react';
import { supabase } from '../supabase';
import { parseZhRefTokens, parseRefTokens, CJK_RE_DETECT } from './refParsing';
import { parseStoredVerses } from './storedText';
import { parseMarkerParts } from './markerParts';

export { parseMarkerParts };

// ── Popup context — lets nested components push new popups ───────────────────
export const PopupContext = React.createContext({ pushPopup: () => {} });

// ── One clickable marker → pushes a new note popup ───────────────────────────
// Split out of VerseWithMarkers so the Reading page can draw the same marker
// inside a verse that also carries highlights (see AnnotatedVerse).
export function MarkerSup({ marker, verseNum, refsMap, verseLang = 'en',
                            contextBook, contextChapter, anchored = false }) {
  const { pushPopup } = React.useContext(PopupContext);
  const ref      = refsMap[`${verseNum}_${marker}`];
  const isLetter = /^[a-z]/i.test(marker);
  return (
    <sup
      className={`bible-ref-sup${ref ? (isLetter ? ' bible-ref-cr' : ' bible-ref-fn') : ''}`}
      title={anchored && ref ? (ref.type === 'cr' ? '📖 ' : '📝 ') + ref.content.slice(0, 80) + (ref.content.length > 80 ? '…' : '') : undefined}
      onClick={ref ? e => {
        e.stopPropagation();
        const rect = anchored ? e.target.getBoundingClientRect() : null;
        pushPopup({
          kind:        'note',
          title:       isLetter ? '📖 Cross-reference' : '📝 Footnote',
          content:     ref.content,
          contentLang: verseLang,
          contextBook,
          contextChapter,
          ...(rect ? { x: rect.left, y: rect.bottom + 6 } : {}),
        });
      } : undefined}
    >
      {marker}
    </sup>
  );
}

// ── Verse with clickable markers → each marker pushes a new note popup ───────
// Inside a popup it is used bare. For verses in the main reading text, pass
// contextBook / contextChapter (so a note's "v. 5" resolves against the verse's
// own chapter) and `anchored`, which opens the note just under the marker and
// gives each marker a hover preview — the same as The Bible's chapter view.
export function VerseWithMarkers({ verseNum, plainText, markedText, refsMap, verseLang = 'en',
                                   contextBook, contextChapter, anchored = false }) {
  if (!markedText) return <>{plainText}</>;
  const parts = parseMarkerParts(markedText);
  return (
    <>
      {parts.map((p, i) => p.kind === 'text'
        ? <React.Fragment key={i}>{p.text}</React.Fragment>
        : <MarkerSup key={i} marker={p.marker} verseNum={verseNum} refsMap={refsMap}
            verseLang={verseLang} contextBook={contextBook} contextChapter={contextChapter}
            anchored={anchored} />)}
    </>
  );
}

// Module-level cache for cross-reference popup content — keyed by book+chapter+lang
// Prevents duplicate DB fetches when the same ref is clicked more than once
const _refCache = new Map();

// ── RefContent: note body — ref links each open a new verse popup ─────────────
// Auto-detects ZH vs EN content and uses the appropriate tokenizer + columns
// contextBook / contextChapter: the host verse's book+chapter for paragraph-aware parsing
export function RefContent({ content, lang: contentLang, contextBook, contextChapter }) {
  const { pushPopup } = React.useContext(PopupContext);
  const [loading, setLoading] = useState({});

  // Determine if this content is Chinese
  const isZh = contentLang === 'zh' || contentLang === 'sc' || CJK_RE_DETECT.test(content);
  const tokens = useMemo(
    () => isZh ? parseZhRefTokens(content) : parseRefTokens(content, contextBook, contextChapter),
    [content, isZh, contextBook, contextChapter]
  );

  async function handleRefClick(token, key) {
    if (loading[key]) return;

    const textCol   = isZh ? (contentLang === 'sc' ? 'text_sc' : 'text_zh') : 'text_en';
    const markedCol = isZh ? (contentLang === 'sc' ? 'text_sc_marked' : 'text_zh_marked') : 'text_en_marked';
    const refsLang  = isZh ? (contentLang === 'sc' ? 'sc' : 'zh') : 'en';
    const cacheKey  = `${token.abbr}_${token.chapter}_${refsLang}`;

    let chData, refsData;
    if (_refCache.has(cacheKey)) {
      ({ chData, refsData } = _refCache.get(cacheKey));
    } else {
      setLoading(prev => ({ ...prev, [key]: true }));
      const [{ data: cd }, { data: rd }] = await Promise.all([
        supabase.from('bible_chapters').select(`${textCol},${markedCol}`)
          .eq('book_abbr', token.abbr).eq('chapter', token.chapter).single(),
        supabase.from('bible_refs').select('verse,marker,type,content')
          .eq('book_abbr', token.abbr).eq('chapter', token.chapter).eq('lang', refsLang),
      ]);
      chData = cd; refsData = rd;
      if (_refCache.size >= 60) _refCache.clear(); // prevent unbounded growth
      _refCache.set(cacheKey, { chData, refsData });
      setLoading(prev => ({ ...prev, [key]: false }));
    }

    const verses = parseStoredVerses(chData?.[textCol] || '')
      .filter(v => v.verse >= token.startVerse && v.verse <= token.endVerse);
    const markedTexts = {};
    const markedRaw = chData?.[markedCol];
    if (markedRaw) {
      markedRaw.split('\n').forEach(line => {
        const mm = line.match(/^(\d+)\s+([\s\S]*)/);
        if (mm) markedTexts[parseInt(mm[1])] = mm[2];
      });
    }
    const refsMap = {};
    (refsData || []).forEach(r => { refsMap[`${r.verse}_${r.marker}`] = { type: r.type, content: r.content }; });

    pushPopup({ kind: 'verse', title: token.label, verses, markedTexts, refsMap,
                verseLang: isZh ? (contentLang === 'sc' ? 'sc' : 'zh') : 'en' });
  }

  return (
    <span>
      {tokens.map((token, i) => {
        if (token.type === 'text') return <span key={i} className="bible-ref-text-segment">{token.text}</span>;
        const key = `${token.abbr}_${token.chapter}_${token.startVerse}-${token.endVerse}`;
        return (
          <React.Fragment key={i}>
            <button className="bible-ref-link"
              onClick={() => handleRefClick(token, key)}
            >
              {token.label}{loading[key] ? ' …' : ''}
            </button>
            {token.noteNum && <sup className="bible-ref-note-num">{token.noteNum}</sup>}
          </React.Fragment>
        );
      })}
    </span>
  );
}
